import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ASSET_LIFECYCLE_STATUSES,
  AssetSchema,
  CUSTODIAN_TYPES,
  EmployeeSchema,
  SupplierSchema,
  type AssetDto,
  type ProjectDto,
  type SupplierDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate } from 'react-router';
import { errorMessage, getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { useUrlList } from '../../shared/ui/url-list';
import { ActiveProjectPicker, ProjectSearchPicker, useProject } from '../crm/common';
import { EmployeePicker, MasterSelect, SupplierPicker } from '../finance/lookups';
import { isAmount } from '../finance/money';
import { UrlDimensionNote } from '../reporting/dimensions';
import { ProcurementSourceFields, type ProcurementSource } from './procurement-source';
import {
  ASSETS,
  classLabel,
  classTree,
  currentCustody,
  Custodian,
  selectable,
  statusLabel,
  useAssetClasses,
  useAssetTypes,
  useMasters,
} from './shared';

/**
 * The asset register (FRD-AST-005, AST-064): every filter the register report names — class (a
 * class includes its sub-classes), location, custodian, department, project, status and cost
 * centre — plus a search over the Asset ID, name and serial number, all answered by the server
 * with paging.
 * The Asset ID is generated by the system (FRD-AST-007), so it is shown and never typed.
 */
export function AssetsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const canCreate = hasCapability(session.data, 'assets.asset:create');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeProjects = hasCapability(session.data, 'projects.project:view');
  const classes = useAssetClasses();
  const types = useAssetTypes();
  const masters = useMasters();
  const [creating, setCreating] = useState(false);
  // Every filter and the page live in the URL (FRD-CORE-038), so a filtered register survives a
  // refresh and a dashboard figure opens the list behind it (FRD-RPT-007).
  const list = useUrlList();
  const custodianType = list.get('custodian_type');
  const params = list.apiParams(
    {
      search: 'search',
      class: 'asset_class_id',
      status: 'status',
      department_id: 'department_id',
      location_id: 'location_id',
      cost_center_id: 'cost_center_id',
      project_id: 'project_id',
      // A dashboard's supplier, employee and asset dimensions (FRD-RPT-010): the asset's
      // supplier, the employee holding it (the page's own custodian choice below wins), and the
      // asset itself.
      supplier_id: 'supplier_id',
      employee_id: 'custodian_employee_id',
      asset_id: 'asset_id',
      in_custody: 'in_custody',
      custodian_type: 'custodian_type',
      custodian: 'custodian_employee_id',
      custodian_department: 'custodian_department_id',
      custodian_location: 'custodian_location_id',
      custodian_project: 'custodian_project_id',
      custodian_third_party: 'custodian_third_party',
    },
    'code',
  );
  const assets = useQuery({
    queryKey: ['assets', 'list', params.toString()],
    queryFn: () => getPage(`${ASSETS}?${params.toString()}`, AssetSchema),
  });
  // A picked employee or project is held in the URL by id and read back for its label.
  const custodian = useEmployee(list.get('custodian'));
  const project = useProject(list.get('project_id') === '' ? null : list.get('project_id'));
  const custodianProject = useProject(
    list.get('custodian_project') === '' ? null : list.get('custodian_project'),
  );
  /** Any filter change starts again from the first page. */
  const refilter = (key: string) => (value: string) => {
    list.set(key, value);
  };
  const classRows = classes.data?.items ?? [];

  const filter = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: readonly { id: string; label: string }[],
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
  const masterOptions = (rows: readonly { id: string; code: string; name: string }[]) =>
    rows.map((row) => ({ id: row.id, label: `${row.code} — ${row.name}` }));

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Asset register"
        description="Every asset, its classification, placement, custodian and status."
        actions={
          <>
            <ExportButton
              recordType="assets.asset"
              href={exportHref(ASSETS, params)}
              label="Export register"
            />
            <ImportButton recordType="assets.asset" />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New asset
              </Button>
            )}
          </>
        }
      />
      <UrlDimensionNote keys={['supplier_id', 'employee_id', 'asset_id']} />
      <Box role="search" aria-label="Filter the register">
        <SearchFilterBar
          label="Search ID, name or serial"
          value={list.get('search')}
          onChange={(value) => {
            list.set('search', value);
          }}
        >
          {filter(
            'Class',
            list.get('class'),
            refilter('class'),
            classTree(classRows).map(({ row, depth }) => ({
              id: row.id,
              label: `${depth === 1 ? '— ' : ''}${row.name}`,
            })),
          )}
          {filter(
            'Status',
            list.get('status'),
            refilter('status'),
            ASSET_LIFECYCLE_STATUSES.map((s) => ({ id: s, label: statusLabel(s) })),
          )}
          {filter('Custody', list.get('in_custody'), refilter('in_custody'), [
            { id: 'true', label: 'In custody' },
          ])}
          {masters.can.departments &&
            filter(
              'Department',
              list.get('department_id'),
              refilter('department_id'),
              masterOptions(masters.departments),
            )}
          {masters.can.locations &&
            filter(
              'Location',
              list.get('location_id'),
              refilter('location_id'),
              masterOptions(masters.locations),
            )}
          {masters.can.costCentres &&
            filter(
              'Cost centre',
              list.get('cost_center_id'),
              refilter('cost_center_id'),
              masterOptions(masters.costCentres),
            )}
          {canSeeProjects && (
            <Stack>
              <ProjectSearchPicker
                label="Project"
                value={project.data ?? null}
                onChange={(value) => {
                  list.set('project_id', value?.id ?? '');
                }}
              />
            </Stack>
          )}
          {/* The custodian of every type (FRD-AST-064 "by … custodian"). */}
          {filter(
            'Custodian type',
            custodianType,
            (value) => {
              // A new type starts its own custodian filter afresh.
              list.setMany({
                custodian_type: value,
                custodian: '',
                custodian_department: '',
                custodian_location: '',
                custodian_project: '',
                custodian_third_party: '',
              });
            },
            CUSTODIAN_TYPES.map((type) => ({ id: type, label: custodianTypeLabel(type) })),
          )}
          {custodianType === 'employee' && canSeeEmployees && (
            <Stack>
              <EmployeePicker
                label="Custodian"
                value={custodian.data ?? null}
                onChange={(value) => {
                  list.set('custodian', value?.id ?? '');
                }}
              />
            </Stack>
          )}
          {custodianType === 'department' &&
            masters.can.departments &&
            filter(
              'Custodian department',
              list.get('custodian_department'),
              refilter('custodian_department'),
              masterOptions(masters.departments),
            )}
          {custodianType === 'location' &&
            masters.can.locations &&
            filter(
              'Custodian location',
              list.get('custodian_location'),
              refilter('custodian_location'),
              masterOptions(masters.locations),
            )}
          {custodianType === 'project' && canSeeProjects && (
            <Stack>
              <ProjectSearchPicker
                label="Custodian project"
                value={custodianProject.data ?? null}
                onChange={(value) => {
                  list.set('custodian_project', value?.id ?? '');
                }}
              />
            </Stack>
          )}
          {custodianType === 'third_party' && (
            <TextField
              size="small"
              label="Third party name"
              value={list.get('custodian_third_party')}
              onChange={(e) => {
                list.set('custodian_third_party', e.target.value);
              }}
            />
          )}
        </SearchFilterBar>
      </Box>
      {assets.isError && <ErrorState message={assets.error.message} />}
      <Section>
        {assets.isPending && <LoadingState label="Loading assets" />}
        {assets.data !== undefined && assets.data.items.length === 0 && (
          <EmptyState title="No assets found" />
        )}
        {assets.data !== undefined && assets.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Assets">
              <TableHead>
                <TableRow>
                  <TableCell>Asset ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Serial number</TableCell>
                  <TableCell>Custodian</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.data.items.map((asset) => (
                  <TableRow key={asset.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Link
                        component={RouterLink}
                        to={`/assets/${asset.id}`}
                        sx={{ fontWeight: 600 }}
                      >
                        {asset.code}
                      </Link>
                    </TableCell>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>{classLabel(classRows, asset.assetClassId)}</TableCell>
                    <TableCell>
                      {types.data?.find((t) => t.valueCode === asset.assetTypeCode)?.label ??
                        asset.assetTypeCode}
                    </TableCell>
                    <TableCell>{asset.serialNumber ?? '—'}</TableCell>
                    <TableCell>
                      <Custodian custody={currentCustody(asset)} />
                    </TableCell>
                    <TableCell>{masters.department(asset.departmentId)}</TableCell>
                    <TableCell>{masters.location(asset.locationId)}</TableCell>
                    <TableCell>
                      <StatusChip status={asset.lifecycleStatus} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={assets.data.total}
              page={list.page}
              rowsPerPage={list.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => {
                list.setPage(next);
              }}
              onRowsPerPageChange={(e) => {
                list.setPageSize(Number.parseInt(e.target.value, 10));
              }}
            />
          </>
        )}
      </Section>
      {creating && (
        <AssetDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`/assets/${saved.id}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

interface FormValues {
  name: string;
  description: string;
  assetTypeCode: string;
  assetClassId: string;
  serialNumber: string;
  manufacturer: string;
  model: string;
  purchaseDate: string;
  purchaseCost: string;
  locationId: string;
  departmentId: string;
  costCenterId: string;
  warrantyProvider: string;
  warrantyExpiryDate: string;
  lifecycleStatus: string;
}

function valuesOf(asset: AssetDto | undefined): FormValues {
  return {
    name: asset?.name ?? '',
    description: asset?.description ?? '',
    assetTypeCode: asset?.assetTypeCode ?? '',
    assetClassId: asset?.assetClassId ?? '',
    serialNumber: asset?.serialNumber ?? '',
    manufacturer: asset?.manufacturer ?? '',
    model: asset?.model ?? '',
    purchaseDate: asset?.purchaseDate ?? '',
    purchaseCost: asset?.purchaseCost ?? '',
    locationId: asset?.locationId ?? '',
    departmentId: asset?.departmentId ?? '',
    costCenterId: asset?.costCenterId ?? '',
    warrantyProvider: asset?.warrantyProvider ?? '',
    warrantyExpiryDate: asset?.warrantyExpiryDate ?? '',
    lifecycleStatus: asset?.lifecycleStatus ?? 'active',
  };
}

const REQUIRED: readonly (keyof FormValues)[] = ['name', 'assetTypeCode', 'assetClassId'];

/**
 * Create (no `asset`) or change one asset. A change sends only what changed, with If-Match; an
 * emptied optional field is sent as null ("clear it"). The purchase cost is typed as text and sent
 * as a decimal string — never a number (SDD §9.2). The lifecycle status is chosen only at creation;
 * later it moves through its own action.
 */
export function AssetDialog({
  asset,
  onClose,
}: {
  asset?: AssetDto;
  onClose: (saved?: AssetDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canSeeSuppliers = hasCapability(session.data, 'procurement.supplier:view');
  const canSeeProjects = hasCapability(session.data, 'projects.project:view');
  const classes = useAssetClasses();
  const types = useAssetTypes();
  const masters = useMasters();
  const creating = asset === undefined;
  const original = valuesOf(asset);
  const [values, setValues] = useState<FormValues>(original);
  const set = (field: keyof FormValues) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };
  // Undefined: the supplier is unchanged. The current one is read on its own, where visible.
  const [supplierChoice, setSupplierChoice] = useState<SupplierDto | null | undefined>(undefined);
  const currentSupplierId = asset?.supplierId ?? null;
  const currentSupplier = useQuery({
    queryKey: ['procurement', 'supplier', currentSupplierId],
    enabled: canSeeSuppliers && currentSupplierId !== null,
    retry: false,
    queryFn: () =>
      getData(`/api/v1/procurement/suppliers/${currentSupplierId ?? ''}`, SupplierSchema),
  });
  const supplier = supplierChoice === undefined ? (currentSupplier.data ?? null) : supplierChoice;
  // The project placement works the same way (FRD-AST-006): undefined is "unchanged".
  const [projectChoice, setProjectChoice] = useState<ProjectDto | null | undefined>(undefined);
  const currentProjectId = asset?.projectId ?? null;
  const currentProject = useProject(currentProjectId);
  const project = projectChoice === undefined ? (currentProject.data ?? null) : projectChoice;
  // The procurement source (FRD-AST-012, AST-016): the order line and GRN line it came from.
  const originalSource: ProcurementSource = {
    purchaseOrderLineId: asset?.purchaseOrderLineId ?? null,
    goodsReceiptLineId: asset?.goodsReceiptLineId ?? null,
  };
  const [source, setSource] = useState<ProcurementSource>(originalSource);

  const body: Record<string, string | null> = {};
  for (const field of Object.keys(values) as (keyof FormValues)[]) {
    const value = values[field].trim();
    if (creating) {
      if (value !== '') body[field] = value;
    } else if (field !== 'lifecycleStatus' && value !== original[field]) {
      body[field] = value === '' && !REQUIRED.includes(field) ? null : value;
    }
  }
  if (creating && supplier !== null) body.supplierId = supplier.id;
  if (
    !creating &&
    supplierChoice !== undefined &&
    (supplierChoice?.id ?? null) !== currentSupplierId
  ) {
    body.supplierId = supplierChoice?.id ?? null;
  }
  if (creating && project !== null) body.projectId = project.id;
  if (
    !creating &&
    projectChoice !== undefined &&
    (projectChoice?.id ?? null) !== currentProjectId
  ) {
    body.projectId = projectChoice?.id ?? null;
  }
  for (const field of ['purchaseOrderLineId', 'goodsReceiptLineId'] as const) {
    if (creating ? source[field] !== null : source[field] !== originalSource[field]) {
      body[field] = source[field];
    }
  }
  const costValid = values.purchaseCost.trim() === '' || isAmount(values.purchaseCost);
  const complete = REQUIRED.every((field) => values[field].trim() !== '') && costValid;

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(ASSETS, body, AssetSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${ASSETS}/${asset.id}`,
            body,
            AssetSchema,
            session.data?.csrfToken ?? '',
            asset.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
      onClose(saved);
    },
  });

  // An active sub-class stays selectable under an inactive class; the current class always shows.
  const classOptions = classTree(classes.data?.items ?? []).filter(
    ({ row }) => row.status === 'active' || row.id === original.assetClassId,
  );
  const typeOptions = (types.data ?? []).filter((t) => t.status === 'active');
  // An asset keeps a retired type: it stays selectable (as its current value) under its label.
  const retiredCurrent = (types.data ?? []).find(
    (t) => t.status !== 'active' && t.valueCode === values.assetTypeCode,
  );
  const text = (field: keyof FormValues, label: string, extra: object = {}) => (
    <TextField
      label={label}
      value={values[field]}
      onChange={(e) => {
        set(field)(e.target.value);
      }}
      {...extra}
    />
  );
  const date = (field: keyof FormValues, label: string) =>
    text(field, label, { type: 'date', slotProps: { inputLabel: { shrink: true } } });

  return (
    <FormDialog
      open
      title={creating ? 'New asset' : `Edit asset ${asset.code}`}
      submitLabel={creating ? 'Create asset' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? errorMessage(save.error) : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {creating ? (
        <Alert severity="info" variant="outlined">
          The Asset ID is generated by the system when the asset is saved.
        </Alert>
      ) : (
        <TextField label="Asset ID" value={asset.code} disabled />
      )}
      {text('name', 'Name', { required: true, autoFocus: creating })}
      <TextField
        select
        label="Class"
        required
        value={values.assetClassId}
        helperText={
          classes.data !== undefined && classOptions.length === 0
            ? 'No active asset class is configured yet.'
            : undefined
        }
        onChange={(e) => {
          set('assetClassId')(e.target.value);
        }}
      >
        {classOptions.map(({ row, depth }) => (
          <MenuItem key={row.id} value={row.id} sx={{ pl: 2 + depth * 3 }}>
            {row.code} — {row.name}
            {row.status === 'active' ? '' : ' (inactive)'}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Asset type"
        required
        value={values.assetTypeCode}
        helperText={
          types.data !== undefined && typeOptions.length === 0
            ? 'No asset type is configured yet.'
            : undefined
        }
        onChange={(e) => {
          set('assetTypeCode')(e.target.value);
        }}
      >
        {typeOptions.map((t) => (
          <MenuItem key={t.valueCode} value={t.valueCode}>
            {t.label}
          </MenuItem>
        ))}
        {values.assetTypeCode !== '' &&
          !typeOptions.some((t) => t.valueCode === values.assetTypeCode) && (
            <MenuItem value={values.assetTypeCode}>
              {retiredCurrent?.label ?? values.assetTypeCode} (inactive)
            </MenuItem>
          )}
      </TextField>
      {creating && (
        <TextField
          select
          label="Initial status"
          required
          value={values.lifecycleStatus}
          onChange={(e) => {
            set('lifecycleStatus')(e.target.value);
          }}
        >
          <MenuItem value="active">Active (in use)</MenuItem>
          <MenuItem value="in_storage">In storage</MenuItem>
        </TextField>
      )}
      {text('description', 'Description', { multiline: true, minRows: 2 })}
      <Divider textAlign="left">
        <Typography variant="subtitle2" component="h3">
          Identification
        </Typography>
      </Divider>
      {text('serialNumber', 'Serial number')}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {text('manufacturer', 'Manufacturer', { fullWidth: true })}
        {text('model', 'Model', { fullWidth: true })}
      </Stack>
      <Divider textAlign="left">
        <Typography variant="subtitle2" component="h3">
          Acquisition
        </Typography>
      </Divider>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {date('purchaseDate', 'Purchase date')}
        {text('purchaseCost', 'Purchase cost (AED)', {
          fullWidth: true,
          error: !costValid,
          helperText: costValid ? 'For example 1250.00' : 'At most 2 decimals, no sign',
          slotProps: { htmlInput: { inputMode: 'decimal' } },
        })}
      </Stack>
      <ProcurementSourceFields
        initialOrderId={asset?.purchaseOrderId ?? null}
        value={source}
        onChange={setSource}
        onAssetClass={set('assetClassId')}
      />
      {canSeeSuppliers ? (
        <SupplierPicker
          value={supplier}
          onChange={setSupplierChoice}
          required={false}
          helperText="Optional"
        />
      ) : (
        <Typography variant="caption" color="text.secondary">
          Choosing a supplier needs permission to view suppliers.
        </Typography>
      )}
      <Divider textAlign="left">
        <Typography variant="subtitle2" component="h3">
          Placement
        </Typography>
      </Divider>
      {/* Each select takes an equal share and may shrink, so the row fits the dialog. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ '& > *': { flex: 1, minWidth: 0 } }}
      >
        <MasterSelect
          label="Location"
          value={values.locationId}
          onChange={set('locationId')}
          options={selectable(masters.locations, original.locationId)}
          disabled={!masters.can.locations}
        />
        <MasterSelect
          label="Department"
          value={values.departmentId}
          onChange={set('departmentId')}
          options={selectable(masters.departments, original.departmentId)}
          disabled={!masters.can.departments}
        />
        <MasterSelect
          label="Cost centre"
          value={values.costCenterId}
          onChange={set('costCenterId')}
          options={selectable(masters.costCentres, original.costCenterId)}
          disabled={!masters.can.costCentres}
        />
      </Stack>
      {canSeeProjects ? (
        <ActiveProjectPicker
          label="Project"
          value={project}
          onChange={setProjectChoice}
          helperText="Optional: an active project"
        />
      ) : (
        <Typography variant="caption" color="text.secondary">
          {currentProjectId === null
            ? 'Choosing a project needs permission to view projects.'
            : 'The asset is placed on a project you cannot view; it is kept as it is.'}
        </Typography>
      )}
      <Divider textAlign="left">
        <Typography variant="subtitle2" component="h3">
          Warranty
        </Typography>
      </Divider>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {text('warrantyProvider', 'Warranty provider', { fullWidth: true })}
        {date('warrantyExpiryDate', 'Warranty expiry')}
      </Stack>
    </FormDialog>
  );
}

/** A custodian type as people read it (ERD §7.4 custodian types). */
function custodianTypeLabel(type: (typeof CUSTODIAN_TYPES)[number]): string {
  return type === 'third_party'
    ? 'Third party'
    : type === 'pool'
      ? 'Pool (unassigned stock)'
      : type.charAt(0).toUpperCase() + type.slice(1);
}

/** An employee named by id in the URL, read back for the picker's label. */
function useEmployee(id: string) {
  const session = useSession();
  return useQuery({
    queryKey: ['hr', 'employee', id],
    enabled: id !== '' && hasCapability(session.data, 'hr.employee:view'),
    retry: false,
    queryFn: () => getData(`/api/v1/hr/employees/${id}`, EmployeeSchema),
  });
}
